import { DestroyRef, inject, Injectable, NgZone, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Platform } from '@ionic/angular/standalone';
import {
  BehaviorSubject,
  Subject,
  Subscription,
  catchError,
  defer,
  EMPTY,
  finalize,
  firstValueFrom,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { GmapService } from './gmap.service';
import { REACTION } from '@constants/app.constants';
import { deepMerge } from '@utils/helpers';
import {
  Client,
  FileResponse,
  IWorldStreamResource,
  My_actions,
  StreamExtendedResource,
  StreamExtendedResourceLocation_source,
  UserTinyResource,
  VideoResource,
  WorldStreamResourceLocation_source,
} from '@api/client';
import { WebSocketController } from '@services/web-socket.controller';
import { STATUS_NUMBERS } from '../avatar.constants';
import { VplayStreamRuntimeMetrics } from '../interfaces/vplay-stream-runtime-metrics.interface';
import { SOCKET_EVENT_NAMES } from '@services/web-socket.interfaces';
import { PageLoadService } from '@app/services/page-load.service';
import { OnlineStateService } from '@services/online-state.service';
import { ContentScrollPosition } from '@avatar/interfaces/content-scroll-position.interface';

const COUNT = 50;
export type ModalStreamsState = 'minimized' | 'half' | 'full';

@Injectable({ providedIn: 'root' })
export class ModalStreamsService {
  private readonly pageLoad = inject(PageLoadService);
  private readonly onlineState = inject(OnlineStateService);
  private readonly destroyRef = inject(DestroyRef);
  #client = inject(Client);
  #gmapController = inject(GmapService);
  #platform = inject(Platform);
  #webSocketController = inject(WebSocketController);
  #ngZone = inject(NgZone);

  public readonly loadFailed = signal(false);
  public scrollPosition: ContentScrollPosition | null = null;

  private readonly _previewHeight = signal(1);

  private _videosSignal = signal<IWorldStreamResource[]>([]);
  private readonly videos$ = toObservable(this._videosSignal);

  private _shiftSubject$ = new BehaviorSubject<number>(0);
  private _isLoading$ = new BehaviorSubject<boolean>(false);
  private _isReload$ = new BehaviorSubject<boolean>(true);
  private _isEnable$ = new BehaviorSubject<boolean>(true);

  private _enterSubject = new Subject<void>();
  private _leaveSubject = new Subject<void>();
  private _reloadSubject = new Subject<void>();
  
  private _isDragging = signal(false);
  private _scrollTo = signal<number>(0);
  private _modalState = signal<ModalStreamsState>('minimized');
  private _isNearBottom = signal<boolean>(false);
  private _returnVideoId: number | null = null;
  private _isMaliRenderer = signal(false);
  private _suppressNextLeave = false;
  private _initialLoadRequested = false;
  private _hasMore = true;
  private seedMergePending = false;
  private activeLoadSubscription: Subscription | null = null;
  private activeLoadRequestId = 0;
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;
  private failedOffset: number | null = null;

  constructor() {
    this.onlineState.reconnected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.loadFailed()) this.retryLoad();
    });
    this.updatePreviewWidth(this.#platform.width());
    //TODO debug only
    // effect(() => {
    //   this.logDuplicateVideos(this._videosSignal());
    // });

    this._isMaliRenderer.set(this.detectMaliRenderer());
    this.subscribeToSocketEvents();
  }

  public getIsDragging() {
    return this._isDragging.asReadonly();
  }

  public setDragging(value: boolean) {
    this._isDragging.set(value);
  }

  public resetScrollTo() {
    this._scrollTo.set(0);
  }

  public getScrollTo() {
    return this._scrollTo.asReadonly();
  }

  public setScrollTo(value: number) {
    this._scrollTo.set(value);
  }

  public setReturnVideoId(videoId: number | null): void {
    this._returnVideoId = typeof videoId === 'number' && Number.isFinite(videoId) ? Number(videoId) : null;
  }

  public consumeReturnVideoId(): number | null {
    const value = this._returnVideoId;
    this._returnVideoId = null;
    return value;
  }

  public peekReturnVideoId(): number | null {
    return this._returnVideoId;
  }

  public flushReturnVideoId(): number | null {
    return this.consumeReturnVideoId();
  }

  public getIsMaliRenderer() {
    return this._isMaliRenderer.asReadonly();
  }

  public isMaliRendererImmediate(): boolean {
    return this._isMaliRenderer();
  }

  public setModalState(state: ModalStreamsState) {
    this._modalState.set(state);
  }

  public getModalState() {
    return this._modalState.asReadonly();
  }

  public setNearBottom(value: boolean): void {
    this._isNearBottom.set(value);
  }

  public getIsNearBottom() {
    return this._isNearBottom.asReadonly();
  }

  public updateVideoLike(id: number, value: REACTION | null) {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const videos = this._videosSignal();
    const idx = videos.findIndex(video => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    const currentReaction = stream.my_actions?.my_reaction ?? null;
    const nextReaction = value === REACTION.LIKE
      ? REACTION.LIKE
      : value === REACTION.DISLIKE
        ? REACTION.DISLIKE
        : null;

    if (currentReaction === nextReaction) {
      return;
    }

    let nextLikes = stream.actions?.likes ?? 0;

    if (nextReaction === REACTION.LIKE) {
      nextLikes += 1;
    } else if (currentReaction === REACTION.LIKE) {
      nextLikes = Math.max(0, nextLikes - 1);
    }

    const my_actions = new My_actions({ ...(stream.my_actions ?? {}), my_reaction: nextReaction });
    const actions = new VideoResource({ ...(stream.actions ?? {}), likes: nextLikes });

    const updatedStream = {
      ...stream,
      my_actions,
      actions
    } as IWorldStreamResource;

    const nextVideos = [...videos];
    nextVideos[idx] = updatedStream;
    this.setVideos(nextVideos);
  }

  public updateVideoLikesCount(id: number, value: number): void {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const videos = this._videosSignal();
    const idx = videos.findIndex(video => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    const likes = Math.max(0, Math.floor(value));

    if (Math.max(0, Math.floor(stream.actions?.likes ?? 0)) === likes) {
      return;
    }

    const actions = new VideoResource({ ...(stream.actions ?? {}), likes });
    const updatedStream = {
      ...stream,
      actions
    } as IWorldStreamResource;

    const nextVideos = [...videos];
    nextVideos[idx] = updatedStream;
    this.setVideos(nextVideos);
  }

  public updateVideoComment(id: number, value: number) {
    const stream = this._videosSignal().find((x) => x.video_id === id);

    if (stream) {
      const current = stream.actions?.comments_count ?? 0;
      if (current === value) {
        return;
      }

      const res = {
        actions: { comments_count: value },
      } as IWorldStreamResource;

      this.updateVideoInList(id, res);
    }
  }

  public updateVideoWatches(id: number, value: number) {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const videos = this._videosSignal();
    const idx = videos.findIndex(video => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    const watches = Math.max(0, Math.floor(value));

    if (Math.max(0, Math.floor(stream.actions?.watches ?? 0)) === watches) {
      return;
    }

    const actions = new VideoResource({ ...(stream.actions ?? {}), watches });

    const updatedStream = {
      ...stream,
      actions
    } as IWorldStreamResource;

    const nextVideos = [...videos];
    nextVideos[idx] = updatedStream;
    this.setVideos(nextVideos);
  }

  public updateStreamLiveViewers(id: number, value: number): void {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const videos = this._videosSignal();
    const idx = videos.findIndex(video => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    const liveViewers = Math.max(0, Math.floor(value));

    if (stream?.['live_viewers'] === liveViewers) {
      return;
    }

    const updatedStream = {
      ...stream,
      live_viewers: liveViewers,
    } as IWorldStreamResource;

    const nextVideos = [...videos];
    nextVideos[idx] = updatedStream;
    this.setVideos(nextVideos);
  }

  public updateStreamLiveState(id: number, live: boolean, forceEnded: boolean = false): void {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      console.warn('[ModalStreams] updateStreamLiveState ignored: invalid id', { id, live });
      return;
    }

    const videos = this._videosSignal();
    const idx = videos.findIndex((video) => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    const wasLive = !!stream.live;
    const wasEnded = !!stream?.['live_ended'];
    const liveEnded = live ? false : (forceEnded ? true : (wasEnded || wasLive));

    if (wasLive === live && wasEnded === liveEnded) {
      console.debug('[ModalStreams] updateStreamLiveState noop', {
        videoId: normalizedId,
        live,
        liveEnded,
      });
      return;
    }

    const updatedStream: IWorldStreamResource = {
      ...stream,
      live,
      live_ended: live ? false : liveEnded,
      ...(live ? {} : { live_viewers: null }),
    };

    const nextVideos = [...videos];
    nextVideos[idx] = updatedStream;
    this.setVideos(nextVideos);
    console.info('[ModalStreams] updateStreamLiveState applied', {
      videoId: normalizedId,
      live,
      liveEnded,
    });
  }

  private updateStreamsLiveByAuthor(authorId: number, live: boolean): void {
    if (!Number.isFinite(authorId)) {
      return;
    }

    const videos = this._videosSignal();
    let changed = false;
    const nextVideos = videos.map((video) => {
      if (video.author_id === authorId && !!video.live !== live) {
        changed = true;
        return {
          ...video,
          live,
          live_ended: live ? false : true,
          ...(live ? {} : { live_viewers: null }),
        } as IWorldStreamResource;
      }
      return video;
    });

    if (changed) {
      this.setVideos(nextVideos);
    }
  }

  private subscribeToSocketEvents(): void {
    this.#webSocketController.streamEvent.subscribe((event) => {
      this.#ngZone.run(() => {
        this.handleStreamEvent(event);
      });
    });
    this.#webSocketController.avatarEvent.subscribe((event) => {
      this.#ngZone.run(() => {
        this.handleAvatarEvent(event);
      });
    });
  }

  private handleStreamEvent(event: { type?: string; data?: any } | null | undefined): void {
    if (!event) {
      return;
    }

    switch (event.type) {
      case SOCKET_EVENT_NAMES.STREAM_LIVE: {
        const videoId = this.normalizeVideoId(event.data?.video_id);
        if (videoId == null) {
          console.warn('[ModalStreams] streamLive ignored: invalid video_id', event.data);
          return;
        }
        const live = !!event.data?.live;
        console.info('[ModalStreams] streamLive received', { videoId, live, data: event.data });
        this.updateStreamLiveState(videoId, live, !live);
        return;
      }
      case SOCKET_EVENT_NAMES.STREAM_LIKES: {
        const videoId = this.normalizeVideoId(event.data?.video_id);
        if (videoId == null) {
          return;
        }
        if (typeof event.data?.likes !== 'number') {
          return;
        }
        this.updateVideoLikesCount(videoId, event.data.likes);
        return;
      }
      case SOCKET_EVENT_NAMES.STREAM_LIVE_VIEWERS: {
        const videoId = this.normalizeVideoId(event.data?.video_id);
        if (videoId == null) {
          return;
        }
        if (typeof event.data?.live_viewers !== 'number') {
          return;
        }
        this.updateStreamLiveViewers(videoId, event.data.live_viewers);
        return;
      }
      case SOCKET_EVENT_NAMES.STREAM_WATCHES: {
        const videoId = this.normalizeVideoId(event.data?.video_id);
        if (videoId == null) {
          return;
        }
        if (typeof event.data?.watches !== 'number') {
          return;
        }
        this.updateVideoWatches(videoId, event.data.watches);
        return;
      }
      default:
        return;
    }
  }

  private handleAvatarEvent(event: { type?: string; data?: any } | null | undefined): void {
    if (!event || event.type !== SOCKET_EVENT_NAMES.AVATAR_STATUS_UPDATED) {
      return;
    }
    const userId = typeof event.data?.userId === 'number' ? event.data.userId : null;
    const status = event.data?.status;
    if (userId == null || status !== STATUS_NUMBERS.OFFLINE) {
      return;
    }
    this.updateStreamsLiveByAuthor(userId, false);
  }

  public markVideoAsWatched(id: number) {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const videos = this._videosSignal();
    const idx = videos.findIndex(video => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    if (stream.my_actions?.i_watched) {
      return;
    }

    const my_actions = new My_actions({ ...(stream.my_actions ?? {}), i_watched: true });

    const updatedStream = {
      ...stream,
      my_actions,
    } as IWorldStreamResource;

    const nextVideos = [...videos];
    nextVideos[idx] = updatedStream;
    this.setVideos(nextVideos);
  }

  public updateUserFollowing(author_id: number, follow: boolean) {
    const videos = this._videosSignal();
    let changed = false;
    const nextVideos = videos.map(item => {
      if (item.author_id !== author_id) {
        return item;
      }

      const current = item.my_actions?.i_subscribed ?? false;
      if (current === follow) {
        return item;
      }

      changed = true;
      const my_actions = new My_actions({ ...(item.my_actions ?? {}), i_subscribed: follow });
      return {
        ...item,
        my_actions,
      } as IWorldStreamResource;
    });

    if (changed) {
      this.setVideos(nextVideos);
    }
  }

  public updateStreamInfo(id: number, title: string, descr: string) {
    const videos = this._videosSignal();
    let changed = false;
    const nextVideos = videos.map(item => {
      if (item.video_id !== id) {
        return item;
      }

      if (item.name === title && item.description === descr) {
        return item;
      }

      changed = true;
      return {
        ...item,
        name: title,
        description: descr,
      } as IWorldStreamResource;
    });

    if (changed) {
      this.setVideos(nextVideos);
    }
  }

  public updateStreamActions(id: number, actions: VideoResource | null, myActions: My_actions | null): void {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }
    if (!actions && !myActions) {
      return;
    }

    const videos = this._videosSignal();
    const idx = videos.findIndex((video) => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    const nextActions = actions ?? stream.actions;
    const nextMyActions = myActions ?? stream.my_actions;

    if (nextActions === stream.actions && nextMyActions === stream.my_actions) {
      return;
    }

    const nextStream: IWorldStreamResource = {
      ...stream,
      actions: nextActions,
      my_actions: nextMyActions,
    };

    const nextVideos = [...videos];
    nextVideos[idx] = nextStream;
    this.setVideos(nextVideos);
  }

  public updateStreamRuntimeMetrics(id: number, patch: VplayStreamRuntimeMetrics): void {
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const videos = this._videosSignal();
    const idx = videos.findIndex((video) => video.video_id === normalizedId);
    if (idx === -1) {
      return;
    }

    const stream = videos[idx];
    const nextActions = this.buildRuntimeActions(stream.actions, patch);
    const hasLive = typeof patch.live === 'boolean';
    const nextLive = hasLive ? patch.live : stream.live;
    const nextLiveEnded = this.resolveRuntimeLiveEnded(stream, patch);
    const nextLiveViewers = this.resolveRuntimeLiveViewers(stream, patch);
    const currentLiveEnded = this.normalizeBooleanFlag(stream['live_ended']);
    const currentLiveViewers = this.normalizeMetric(stream['live_viewers']);

    const liveChanged = nextLive !== stream.live;
    const liveEndedChanged = nextLiveEnded !== currentLiveEnded;
    const liveViewersChanged = nextLiveViewers !== currentLiveViewers;
    const actionsChanged = nextActions !== stream.actions;

    if (!liveChanged && !liveEndedChanged && !liveViewersChanged && !actionsChanged) {
      return;
    }

    const nextStream: IWorldStreamResource = {
      ...stream,
      live: nextLive,
      actions: nextActions,
    };

    if (liveViewersChanged) {
      nextStream['live_viewers'] = nextLiveViewers;
    }

    if (liveEndedChanged) {
      nextStream['live_ended'] = nextLiveEnded;
    }

    const nextVideos = [...videos];
    nextVideos[idx] = nextStream;
    this.setVideos(nextVideos);
  }

  public deleteStream(id: number) {
    const videos = this._videosSignal();
    const nextVideos = videos.filter(item => item.video_id !== id);
    if (nextVideos.length !== videos.length) {
      this.setVideos(nextVideos);
    }
  }

  public getIsEnable() {
    return this._isEnable$.asObservable();
  }

  public setDisable() {
    this._isEnable$.next(false);
  }

  public setEnable() {
    this._isEnable$.next(true);
  }

  public getPreviewHeight(): number {
    return this._previewHeight();
  }

  public updatePreviewWidth(width: number): void {
    if (width <= 0) return;

    const screenWidth = Math.min(width, 620);
    this._previewHeight.set(Math.max(1, Math.round(((screenWidth - 2 * 8 - 10) / 2) * (16 / 9)) + 10));
  }

  public getVideoList() {
    return this.videos$;
  }

  public getVideoListSignal() {
    return this._videosSignal.asReadonly();
  }

  public ensureInitialLoad(): void {
    if (this._initialLoadRequested) {
      return;
    }

    const hasVideos = this._videosSignal().length > 0;
    this._initialLoadRequested = true;

    if (hasVideos) {
      return;
    }

    this.reloadVideos();
  }

  public seedWithStream(
    stream: StreamExtendedResource | null | undefined,
    options?: { loadMore?: boolean }
  ): void {
    const adapted = this.adaptExtendedStream(stream);
    if (!adapted) {
      return;
    }

    this.resetList();
    this.seedMergePending = true;
    this.setVideos([adapted]);

    if (options?.loadMore) {
      const videoId = this.normalizeVideoId(adapted.video_id);
      this.loadNext(videoId);
    }
  }

  public getIsEmpty() {
    return this.videos$.pipe(map((x) => x.length === 0));
  }

  public hasMore(): boolean {
    return this._hasMore;
  }

  public getIsRefresh() {
    return this._isReload$.asObservable();
  }

  public setReload() {
    this._isReload$.next(true);
  }

  public setRefresh() {
    this._isLoading$.next(true);
  }

  public isLoading() {
    return this._isLoading$.asObservable();
  }

  public resetList() {
    this.scrollPosition = null;
    this.cancelActiveLoad();
    this.failedOffset = null;
    this.loadFailed.set(false);
    this.setVideos([]);
    this._shiftSubject$.next(0);
    this._initialLoadRequested = false;
    this._hasMore = true;
    this.seedMergePending = false;
  }

  public async loadSingleForSsr(video?: number): Promise<IWorldStreamResource | null> {
    const normalizedVideoId = this.normalizeVideoId(video ?? null);
    if (normalizedVideoId == null) {
      return null;
    }

    try {
      // 1) Попробуем WorldIndex, он отдаёт user/author.
      const world = await firstValueFrom(
        this.#client.videosWorldIndex(undefined, undefined, 1, 0, normalizedVideoId, [])
      );
      const worldStream = world?.data?.[0];
      if (worldStream) {
        this.setVideos([worldStream]);
        this._shiftSubject$.next(1);
        this._initialLoadRequested = true;
        return worldStream;
      }

      // 2) Фолбэк — старый videosGet.
      const response = await firstValueFrom(this.#client.videosGet(normalizedVideoId));
      const stream = this.adaptExtendedStream(response, normalizedVideoId);
      if (!stream) {
        return null;
      }
      this.setVideos([stream]);
      this._shiftSubject$.next(1);
      this._initialLoadRequested = true;
      return stream;
    } catch (error) {
      console.error('[ModalStreamsService] loadSingleForSsr failed', {
        videoId: normalizedVideoId,
        error,
      });
      return null;
    }
  }

  public async loadSingleForDirectLink(video?: number): Promise<IWorldStreamResource | null> {
    const normalizedVideoId = this.normalizeVideoId(video ?? null);
    if (normalizedVideoId == null) {
      return null;
    }

    try {
      const world = await firstValueFrom(
        this.#client.videosWorldIndex(undefined, undefined, 1, 0, normalizedVideoId, [])
      );
      const worldStream = world?.data?.[0];
      if (worldStream) {
        this.seedMergePending = true;
        this._shiftSubject$.next(0);
        this._hasMore = true;
        this._initialLoadRequested = false;
        this.setVideos([worldStream]);
        return worldStream;
      }

      const response = await firstValueFrom(this.#client.videosGet(normalizedVideoId));
      const stream = this.adaptExtendedStream(response, normalizedVideoId);
      if (!stream) {
        return null;
      }

      this.seedMergePending = true;
      this._shiftSubject$.next(0);
      this._hasMore = true;
      this._initialLoadRequested = false;
      this.setVideos([stream]);
      return stream;
    } catch (error) {
      console.error('[ModalStreamsService] loadSingleForDirectLink failed', {
        videoId: normalizedVideoId,
        error,
      });
      return null;
    }
  }

  public loadDirectLink$(video?: number | null): Observable<void> {
    const normalizedVideoId = this.normalizeVideoId(video ?? null);
    if (normalizedVideoId == null) {
      return defer(() => {
        this.resetList();
        this.reloadVideos();
        return of(void 0);
      });
    }

    return defer(() => {
      this.resetList();
      return this.loadSingleForDirectLink$(normalizedVideoId).pipe(
        switchMap(() => this.loadMoreVideosOnce$(0, normalizedVideoId)),
        map(() => void 0),
      );
    });
  }

  public loadSingleForDirectLink$(video?: number | null): Observable<IWorldStreamResource | null> {
    const normalizedVideoId = this.normalizeVideoId(video ?? null);
    if (normalizedVideoId == null) {
      return of(null);
    }

    const worldIndex$ = this.#client
      .videosWorldIndex(undefined, undefined, 1, 0, normalizedVideoId, [])
      .pipe(
        take(1),
        map((world) => world?.data?.[0] ?? null),
      );

    return worldIndex$.pipe(
      switchMap((worldStream) => {
        if (worldStream) {
          return of(worldStream);
        }

        return this.#client
          .videosGet(normalizedVideoId)
          .pipe(
            take(1),
            map((stream) => this.adaptExtendedStream(stream, normalizedVideoId)),
          );
      }),
      tap((stream) => {
        if (!stream) {
          return;
        }

        this.seedMergePending = true;
        this._shiftSubject$.next(0);
        this._hasMore = true;
        this._initialLoadRequested = false;
        this.setVideos([stream]);
      }),
      catchError((error) => {
        console.error('[ModalStreamsService] loadSingleForDirectLink$ failed', {
          videoId: normalizedVideoId,
          error,
        });
        return of(null);
      }),
    );
  }

  private loadMoreVideosOnce$(offsetPage: number, video?: number): Observable<IWorldStreamResource[]> {
    if (this._isLoading$.getValue() || !this._isEnable$.getValue() || !this._hasMore) {
      return of([]);
    }

    this.loadFailed.set(false);
    this._isLoading$.next(true);

    return this.getStreams({ offset: offsetPage, video }).pipe(
      take(1),
      map((newStreams) => this.normalizeStreams(newStreams.data ?? [])),
      tap((loaded) => {
        this.#ngZone.run(() => {
          if (loaded.length === 0 || loaded.length < COUNT) {
            this._hasMore = false;
          }

          const oldStreams = this._videosSignal();
          const nextStreams = this.seedMergePending
            ? this.mergeSeededStreams(oldStreams, loaded)
            : [...oldStreams, ...loaded];

          this.seedMergePending = false;
          this.setVideos(nextStreams);
          this._isReload$.next(false);

          const nextShift = loaded.length === 0 ? offsetPage : offsetPage + 1;
          this._shiftSubject$.next(nextShift);
        });
      }),
      catchError((err) => {
        console.log(err);
        this._initialLoadRequested = false;
        this.loadFailed.set(true);
        return of([]);
      }),
      finalize(() => {
        this._isLoading$.next(false);
      }),
    );
  }

  private adaptExtendedStream(
    stream: StreamExtendedResource | null | undefined,
    fallbackVideoId?: number
  ): IWorldStreamResource | null {
    if (!stream) {
      return null;
    }

    const videoId = stream.video_id ?? stream.video?.id ?? fallbackVideoId;
    const sourceUser = this.extractUserData(stream.user) ?? this.extractUserData(this.extractVideoUser(stream));
    const authorIdRaw = stream['author_id'];
    const authorId = sourceUser?.id ?? this.normalizeVideoId(authorIdRaw) ?? null;

    const actions = new VideoResource({
      id: videoId,
      likes: stream.video?.likes,
      dislikes: stream.video?.dislikes,
      watches: stream.video?.watches,
      comments_count: stream.video?.comments_count ?? stream.comments_count,
    });

    const myActions = new My_actions({
      my_reaction: stream.my_reaction,
      i_watched: stream.i_watched,
      i_subscribed: stream.i_subscribed,
    });

    const avatar = this.normalizeAvatar(sourceUser?.avatar);
    const photoUrl = this.normalizeAvatarUrl(sourceUser?.photo);
    const avatarUrl = this.normalizeAvatarUrl(sourceUser?.avatar_url);
    const user = sourceUser
      ? new UserTinyResource({
          id: sourceUser.id,
          name: sourceUser.name,
          surname: sourceUser.surname,
          official: sourceUser.official,
          avatar: avatar ?? undefined,
        })
      : undefined;
    if (user) {
      if (photoUrl) {
        user['photo'] = photoUrl;
      }
      if (avatarUrl) {
        user['avatar_url'] = avatarUrl;
      }
    }

    const extra = this.extractExtraFields(stream);
    const locationSource = this.normalizeLocationSource(stream.location_source);
    const liveViewers = this.normalizeMetric(stream['live_viewers']);
    const liveEnded = this.normalizeBooleanFlag(stream['live_ended']);

    const worldStream: IWorldStreamResource = {
      ...extra,
      video_id: videoId,
      author_id: authorId ?? undefined,
      live: stream.live,
      ...(liveViewers !== null ? { live_viewers: liveViewers } : {}),
      ...(liveEnded !== null ? { live_ended: liveEnded } : {}),
      actions,
      name: stream.name ?? stream.video?.name,
      description: stream.description ?? stream.place ?? undefined,
      location: stream.place ?? undefined,
      place: stream.place ?? undefined,
      location_source: locationSource,
      my_actions: myActions,
      user,
    };

    return this.normalizeStream(worldStream);
  }

  private extractVideoUser(stream: StreamExtendedResource): unknown {
    const videoValue = stream['video'];
    if (!this.isRecord(videoValue)) {
      return undefined;
    }
    return videoValue['user'];
  }

  private extractUserData(value: unknown): UserLike | null {
    if (!this.isRecord(value)) {
      return null;
    }
    const id = typeof value['id'] === 'number' ? value['id'] : undefined;
    const name = typeof value['name'] === 'string' ? value['name'] : undefined;
    const surname = typeof value['surname'] === 'string' ? value['surname'] : undefined;
    const official = typeof value['official'] === 'boolean' ? value['official'] : undefined;
    const avatar = value['avatar'];
    const photo = value['photo'];
    const avatarUrl = value['avatar_url'];

    return {
      id,
      name,
      surname,
      official,
      avatar,
      photo,
      avatar_url: avatarUrl,
    };
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value != null && typeof value === 'object' && !Array.isArray(value);
  }

  private normalizeAvatar(value: unknown): FileResponse | undefined {
    if (!this.isRecord(value)) {
      return undefined;
    }
    return FileResponse.fromJS(value);
  }

  private normalizeAvatarUrl(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private normalizeLocationSource(
    value: StreamExtendedResourceLocation_source | undefined
  ): WorldStreamResourceLocation_source | undefined {
    if (value === StreamExtendedResourceLocation_source.Gps) {
      return WorldStreamResourceLocation_source.Gps;
    }
    if (value === StreamExtendedResourceLocation_source.Ip) {
      return WorldStreamResourceLocation_source.Ip;
    }
    return undefined;
  }

  private extractExtraFields(stream: StreamExtendedResource): Record<string, unknown> {
    const extra: Record<string, unknown> = {};
    const fileValue = stream['file'];
    if (fileValue !== undefined) {
      extra['file'] = fileValue;
    }
    const fileUrl = stream['file_url'];
    if (typeof fileUrl === 'string' && fileUrl.trim().length > 0) {
      extra['file_url'] = fileUrl.trim();
    }
    const fileId = stream['file_id'];
    if (typeof fileId === 'number' && Number.isFinite(fileId)) {
      extra['file_id'] = fileId;
    } else if (typeof fileId === 'string' && fileId.trim().length > 0) {
      extra['file_id'] = fileId.trim();
    }
    const fileUserId = stream['file_user_id'];
    if (typeof fileUserId === 'number' && Number.isFinite(fileUserId)) {
      extra['file_user_id'] = fileUserId;
    } else if (typeof fileUserId === 'string' && fileUserId.trim().length > 0) {
      extra['file_user_id'] = fileUserId.trim();
    }
    const videoValue = stream['video'];
    if (videoValue !== undefined) {
      extra['video'] = videoValue;
    }
    if (stream.location) {
      extra['location_coordinates'] = stream.location;
    }
    if (typeof stream.place === 'string' && stream.place.trim().length > 0) {
      extra['place'] = stream.place.trim();
    }
    return extra;
  }

  public loadNext(video?: number) {
    if (this.loadFailed()) return;
    const offset = this._shiftSubject$.getValue();
    const normalizedVideoId = this.normalizeVideoId(video);

    const started = this.loadMoreVideos(offset, normalizedVideoId);
    if (started) {
      this._shiftSubject$.next(offset + 1);
    }
  }

  public reloadVideos() {
    this.scrollPosition = null;
    this.cancelActiveLoad();
    this.notifyReload();
    this.setVideos([]);
    this._shiftSubject$.next(0);
    this._hasMore = true;
    this.seedMergePending = false;
    this.failedOffset = null;

    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer);
      this.reloadTimer = null;
    }

    this.reloadTimer = setTimeout(() => {
      this.reloadTimer = null;
      const started = this.loadMoreVideos(0);
      this._shiftSubject$.next(started ? 1 : 0);
    }, 0);
  }

  public retryLoad(): void {
    if (this._isLoading$.value) return;
    const offset = this.failedOffset;
    if (offset === null) {
      this.reloadVideos();
    } else if (this.loadMoreVideos(offset)) {
      this._shiftSubject$.next(offset + 1);
    }
  }

  public notifyEnter() {
    this._enterSubject.next();
  }

  public notifyLeave() {
    if (this._suppressNextLeave) {
      this._suppressNextLeave = false;
      return;
    }
    this._leaveSubject.next();
  }

  public notifyReload() {
    this._reloadSubject.next();
  }

  public suppressNextLeave(): void {
    this._suppressNextLeave = true;
  }

  public onEnter() {
    return this._enterSubject.asObservable();
  }

  public onLeave() {
    return this._leaveSubject.asObservable();
  }

  public onReload() {
    return this._reloadSubject.asObservable();
  }

  private getStreams(opts: { offset?: number; video?: number }) {
    const lat = this.#gmapController.centerLat;
    const long = this.#gmapController.centerLon;
    const count = COUNT;
    const offset = (opts.offset || 0) * count;
    const video_id = opts.video || undefined;

    return this.pageLoad.read(this.#client.videosWorldIndex(long, lat, count, offset, video_id, []));
  }

  private setVideos(videos: IWorldStreamResource[]) {
    const normalized = this.normalizeStreams(videos);
    this._videosSignal.set(normalized);
  }

  private logDuplicateVideos(videos: IWorldStreamResource[]) {
    const list = videos ?? [];
    if (list.length === 0) {
      console.log('[ModalStreamsService] duplicate videos: 0; video_id: none');
      return;
    }

    const counters = new Map<number, number>();

    for (const { video_id } of list) {
      if (video_id == null) {
        continue;
      }

      counters.set(video_id, (counters.get(video_id) ?? 0) + 1);
    }

    const duplicates = Array.from(counters.entries()).filter(
      ([, count]) => count > 1
    );
    const duplicateItemsCount = duplicates.reduce(
      (total, [, count]) => total + (count - 1),
      0
    );
    const duplicateDetails =
      duplicates.map(([id, count]) => `${id}(x${count})`).join(', ') || 'none';

    console.log(
      `[ModalStreamsService] duplicate videos: ${duplicateItemsCount}; video_id: ${duplicateDetails}`
    );
  }

  private normalizeStreams(streams: IWorldStreamResource[]): IWorldStreamResource[] {
    if (!Array.isArray(streams)) {
      return [];
    }

    return streams.map(stream => this.normalizeStream(stream));
  }

  private normalizeStream(stream: IWorldStreamResource): IWorldStreamResource {
    if (!stream) {
      return stream;
    }

    const normalizedId = this.normalizeVideoId(stream.video_id);

    if (normalizedId !== stream.video_id) {
      return {
        ...stream,
        video_id: normalizedId
      };
    }

    return stream;
  }

  private buildRuntimeActions(
    actions: VideoResource | undefined,
    patch: VplayStreamRuntimeMetrics
  ): VideoResource | undefined {
    const nextLikes = this.normalizeMetric(patch.likes);
    const nextWatches = this.normalizeMetric(patch.watches);
    const nextCommentsCount = this.normalizeMetric(patch.commentsCount);

    if (nextLikes === null && nextWatches === null && nextCommentsCount === null) {
      return actions;
    }

    const base = actions ?? new VideoResource({});
    const likes = nextLikes ?? this.normalizeMetric(base.likes);
    const watches = nextWatches ?? this.normalizeMetric(base.watches);
    const commentsCount = nextCommentsCount ?? this.normalizeMetric(base.comments_count);

    if (
      likes === this.normalizeMetric(actions?.likes)
      && watches === this.normalizeMetric(actions?.watches)
      && commentsCount === this.normalizeMetric(actions?.comments_count)
    ) {
      return actions;
    }

    return new VideoResource({
      ...(base ?? {}),
      likes: likes ?? undefined,
      watches: watches ?? undefined,
      comments_count: commentsCount ?? undefined,
    });
  }

  private resolveRuntimeLiveEnded(
    stream: IWorldStreamResource,
    patch: VplayStreamRuntimeMetrics
  ): boolean | null {
    if (typeof patch.liveEnded === 'boolean') {
      return patch.liveEnded;
    }
    if (patch.live === true) {
      return false;
    }
    if (patch.live === false) {
      return this.normalizeBooleanFlag(stream['live_ended']) ?? !!stream.live;
    }
    return this.normalizeBooleanFlag(stream['live_ended']);
  }

  private resolveRuntimeLiveViewers(
    stream: IWorldStreamResource,
    patch: VplayStreamRuntimeMetrics
  ): number | null {
    const liveViewers = this.normalizeMetric(patch.liveViewers);
    if (liveViewers !== null) {
      return liveViewers;
    }
    if (patch.live === false) {
      return null;
    }
    return this.normalizeMetric(stream['live_viewers']);
  }

  private normalizeVideoId(value: number | string | null | undefined): number | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined;
    }

    if (typeof value === 'string') {
      const parsed = Number(value.trim());
      return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
  }

  private normalizeMetric(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(0, Math.floor(value));
    }

    if (typeof value === 'string') {
      const parsed = Number(value.trim());
      if (Number.isFinite(parsed)) {
        return Math.max(0, Math.floor(parsed));
      }
    }

    return null;
  }

  private normalizeBooleanFlag(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    return null;
  }

  private updateVideoInList(id: number, patch: Partial<IWorldStreamResource>) {
    const videos = this._videosSignal();
    const normalizedId = this.normalizeVideoId(id);
    if (normalizedId == null) {
      return;
    }

    const idx = videos.findIndex((v) => v.video_id === normalizedId);

    if (idx !== -1) {
      const updated = deepMerge({ ...videos[idx] }, patch);
      const newArr = [...videos];

      newArr[idx] = updated;
      this.setVideos(newArr);
    }
  }

  private loadMoreVideos(offset: number, video?: number): boolean {
    if (this._isLoading$.getValue() || !this._isEnable$.getValue() || !this._hasMore) {
      return false;
    }

    this.cancelActiveLoad();

    this.loadFailed.set(false);
    this._isLoading$.next(true);
    const requestId = ++this.activeLoadRequestId;

    this.activeLoadSubscription = this.getStreams({ offset, video })
      .pipe(
        take(1),
        catchError((err) => {
          console.log(err);
          this._initialLoadRequested = false;
          this.failedOffset = offset;
          this.loadFailed.set(true);
          return EMPTY;
        }),
        finalize(() => {
          if (this.activeLoadRequestId !== requestId) {
            return;
          }
          this.activeLoadSubscription = null;
          this._isLoading$.next(false);
        })
      )
      .subscribe((newStreams) => {
        if (this.activeLoadRequestId !== requestId) {
          return;
        }

        this.#ngZone.run(() => {
          const oldStreams = this._videosSignal();
          const loaded = this.normalizeStreams(newStreams.data ?? []);

          if (loaded.length === 0) {
            this._hasMore = false;
            const offset = this._shiftSubject$.getValue();
            this._shiftSubject$.next(offset - 1);
          }

          if (loaded.length < COUNT) {
            this._hasMore = false;
          }

          const nextStreams = this.seedMergePending
            ? this.mergeSeededStreams(oldStreams, loaded)
            : [...oldStreams, ...loaded];

          this.seedMergePending = false;

          this.setVideos(nextStreams);
          this._isReload$.next(false);
        });
      });

    return true;
  }

  private cancelActiveLoad(): void {
    if (!this.activeLoadSubscription) {
      return;
    }

    this.activeLoadRequestId += 1;
    try {
      this.activeLoadSubscription.unsubscribe();
    } catch (error) {
      console.warn('[ModalStreamsService] cancelActiveLoad failed', error);
    }
    this.activeLoadSubscription = null;
    this._isLoading$.next(false);
  }

  private mergeSeededStreams(
    existing: IWorldStreamResource[],
    loaded: IWorldStreamResource[]
  ): IWorldStreamResource[] {
    if (!existing.length) {
      return loaded;
    }
    if (!loaded.length) {
      return existing;
    }

    const loadedById = new Map<number, IWorldStreamResource>();
    for (const item of loaded) {
      const id = this.normalizeVideoId(item.video_id);
      if (id != null) {
        loadedById.set(id, item);
      }
    }

    const usedIds = new Set<number>();
    const result: IWorldStreamResource[] = [];

    for (const item of existing) {
      const id = this.normalizeVideoId(item.video_id);
      const replacement = id != null ? loadedById.get(id) : undefined;
      if (replacement) {
        result.push(this.mergeSeededStream(item, replacement));
      } else {
        result.push(item);
      }
      if (id != null) {
        usedIds.add(id);
      }
    }

    for (const item of loaded) {
      const id = this.normalizeVideoId(item.video_id);
      if (id != null && usedIds.has(id)) {
        continue;
      }
      result.push(item);
      if (id != null) {
        usedIds.add(id);
      }
    }

    return result;
  }

  private mergeSeededStream(
    existing: IWorldStreamResource,
    loaded: IWorldStreamResource
  ): IWorldStreamResource {
    const merged: IWorldStreamResource = {
      ...loaded,
    };

    const mergedFile = this.pickDefinedValue(loaded['file'], existing['file']);
    if (mergedFile !== undefined) {
      merged['file'] = mergedFile;
    }

    const mergedVideo = this.pickDefinedValue(loaded['video'], existing['video']);
    if (mergedVideo !== undefined) {
      merged['video'] = mergedVideo;
    }

    const mergedFileUrl = this.pickNonEmptyString(loaded['file_url'], existing['file_url']);
    if (mergedFileUrl) {
      merged['file_url'] = mergedFileUrl;
    }

    const mergedFileId = this.pickDefinedValue(loaded['file_id'], existing['file_id']);
    if (mergedFileId !== undefined) {
      merged['file_id'] = mergedFileId;
    }

    const mergedFileUserId = this.pickDefinedValue(loaded['file_user_id'], existing['file_user_id']);
    if (mergedFileUserId !== undefined) {
      merged['file_user_id'] = mergedFileUserId;
    }

    return merged;
  }

  private pickDefinedValue(
    primary: unknown,
    fallback: unknown
  ): unknown | undefined {
    if (primary !== null && primary !== undefined) {
      return primary;
    }
    if (fallback !== null && fallback !== undefined) {
      return fallback;
    }
    return undefined;
  }

  private pickNonEmptyString(primary: unknown, fallback: unknown): string | undefined {
    const primaryValue = this.normalizeText(primary);
    if (primaryValue) {
      return primaryValue;
    }
    const fallbackValue = this.normalizeText(fallback);
    return fallbackValue ?? undefined;
  }

  private normalizeText(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private detectMaliRenderer(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }

    let canvas: HTMLCanvasElement | null = null;
    try {
      canvas = document.createElement('canvas');
      const gl =
        (canvas.getContext('webgl') ||
          canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;

      if (!gl) {
        return false;
      }

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (!debugInfo) {
        return false;
      }

      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      if (typeof renderer === 'string' && renderer.toLowerCase().includes('mali')) {
        return true;
      }

      return false;
    } catch (error) {
      console.warn('[ModalStreamsService] failed to detect GPU renderer', error);
      return false;
    } finally {
      if (canvas && typeof canvas.remove === 'function') {
        canvas.remove();
      }
    }
  }

}

type UserLike = {
  id?: number;
  name?: string;
  surname?: string;
  official?: boolean;
  avatar?: unknown;
  photo?: unknown;
  avatar_url?: unknown;
};
